// Microbe RTS — smart-tap input + camera gestures (DOM/pointer layer).
//
// One-thumb portrait control surface. Owns ALL pointer interpretation over the game canvas:
//   • one-finger hold+steer -> edge-scroll: the camera pulls TOWARD the finger, faster near the edge
//   • two-finger pinch -> zoom about the pinch centroid (cam.zoomAt) — raw pointer events
//   • single TAP       -> a CONTEXT command resolved by tap-magnetism (CAMERA.tapMagnetRadiusPx)
//                         against the sim's pick helpers, ranked by config TAP_PRIORITY.
//
// It talks to the sim ONLY through sim.command / sim.pick* / sim.state (read), and to the
// renderer ONLY through the Camera contract below. It never mutates game state directly.
//
// Cross-UI coordination (this layer <-> hud.ts / evolve.ts) rides window CustomEvents so the
// three mounts stay decoupled (each is handed only what its signature allows):
//   IN   'microbe:recenter'            {}                     -> recenter camera on your Home
//   IN   'microbe:rally' detail        { nodeId }             -> arm "next tap sets this node's rally"
//   OUT  'microbe:nodeselect' detail   { nodeId | null }      -> tell the HUD which node panel to show
//
// verbatimModuleSyntax: types imported with `import type`.

import { attachInput } from '../../src/input.js';
import { CAMERA, TAP_PRIORITY, HAPTIC, SELECT } from '../config.js';
import type { Sim, Vec, Unit, WorldNode, OrderSnapshot, Side } from '../types.js';
import type { Camera } from '../render/camera.js';
import type { FloatingJoystick } from './floating-joystick.js';

/** What this input layer needs from a haptic sink (kit's `haptic`, signature-compatible). */
export type HapticFn = (pattern: number | number[]) => void;

interface Pt {
  x: number;
  y: number;
}

/**
 * Wire smart-tap + gestures onto the canvas element.
 * @param canvas  the element the renderer draws into (also the gesture surface)
 * @param sim     authoritative sim (commands + read-only picks)
 * @param cam     camera contract from ../render/camera.js
 * @param haptic  kit haptic sink (fires the config HAPTIC vocabulary)
 */
export function attachGameInput(
  canvas: HTMLElement,
  sim: Sim,
  cam: Camera,
  haptic: HapticFn,
  fjoy: FloatingJoystick,
  viewSide: Side = 'you',
): { destroy(): void } {
  // The LOCAL player's side (solo 'you'; PvP the session's side) — every "own unit / own node"
  // pick + selection read is scoped to it, so a player selects + commands ONLY their OWN colony.
  // foeSide is the opposite colony (the focus/siege target). Presentation only — never hashed.
  const foeSide: Side = viewSide === 'you' ? 'bot' : 'you';
  // ---- pinch state (raw multi-pointer, layered under attachInput) ----
  const pointers = new Map<number, Pt>();
  let pinching = false;
  let lastPinchDist = 0;
  let suppressNextTap = false;
  // ---- floating joystick: which pointer is currently steering (-1 = none) ----
  let joyPid = -1;
  // While a node control panel is open the joystick isn't wanted (and open-water taps should close
  // the panel, not steer); hud.ts broadcasts 'microbe:panel' {open} as it toggles.
  let panelOpen = false;
  // Long-press on terrain WHILE troops are selected raises the nav joystick (a quick tap stays a
  // move order). These track the pending hold; it's cancelled by movement, lift, or a 2nd finger.
  let holdPid = -1;
  let holdTimer = 0;
  const holdOrigin = { x: 0, y: 0 }; // client coords, for placing the pad
  const holdOriginLocal = { x: 0, y: 0 }; // canvas-local, for the move-cancel check
  const HOLD_MS = 320; // press duration that flips a move-tap into a nav-joystick
  const HOLD_SLOP = 12; // move farther than this before HOLD_MS and it's a drag, not a hold

  // Camera steering now lives in the on-screen joystick pad (ui/joystick.ts). The map surface is
  // TAP-ONLY (commands) plus two-finger pinch-zoom; a one-finger drag on the map is a no-op.

  // ---- rally-arm (set by the HUD's Rally chip; consumed by the next tap) ----
  let armedRallyNodeId: string | null = null;
  // ---- turret-placement arm (set by the HUD's Build-Turret chip; the next tap places one) ----
  let armedPlaceTurret = false;

  const localOf = (e: PointerEvent): Pt => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const centroid = (): Pt => {
    let cx = 0;
    let cy = 0;
    for (const p of pointers.values()) {
      cx += p.x;
      cy += p.y;
    }
    const n = pointers.size || 1;
    return { x: cx / n, y: cy / n };
  };

  const twoFingerDist = (): number => {
    const it = pointers.values();
    const a = it.next().value as Pt | undefined;
    const b = it.next().value as Pt | undefined;
    if (!a || !b) return 0;
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const clearHold = (): void => {
    if (holdTimer) {
      clearTimeout(holdTimer);
      holdTimer = 0;
    }
    holdPid = -1;
  };
  // Tear down a live steer pad from ANY path (lift / pinch / panel) and drop the tap-suppress, so a
  // hold that ended as a drag can't leak suppressNextTap into the next tap.
  const endJoystick = (): void => {
    if (joyPid === -1) return;
    fjoy.end();
    joyPid = -1;
    suppressNextTap = false;
  };

  // ---------- raw pointer listeners: pinch-zoom + floating joystick ----------
  const onRawDown = (e: PointerEvent) => {
    const lp = localOf(e);
    pointers.set(e.pointerId, lp);
    if (pointers.size === 2) {
      pinching = true;
      lastPinchDist = twoFingerDist();
      endJoystick(); // a second finger means pinch-zoom — drop the steer pad
      clearHold(); // ...and any pending long-press
      return;
    }
    if (pointers.size === 1 && joyPid === -1 && isOpenTerrain(lp)) {
      if (sim.state.selectionBySide[viewSide].length === 0) {
        // Nothing selected: open terrain steers immediately.
        fjoy.begin(e.clientX, e.clientY);
        joyPid = e.pointerId;
        unlockCamera(); // a manual pan breaks a camera lock (selection is untouched)
      } else {
        // Troops selected: a quick tap is a MOVE order; a HOLD raises the nav joystick instead.
        holdPid = e.pointerId;
        holdOrigin.x = e.clientX;
        holdOrigin.y = e.clientY;
        holdOriginLocal.x = lp.x;
        holdOriginLocal.y = lp.y;
        holdTimer = window.setTimeout(() => {
          holdTimer = 0;
          if (holdPid !== e.pointerId) return;
          holdPid = -1;
          fjoy.begin(holdOrigin.x, holdOrigin.y);
          joyPid = e.pointerId;
          unlockCamera(); // raising the steer pad = manual pan → drop the camera lock (keep selection)
          suppressNextTap = true; // the release must NOT also fire a move order
          haptic(HAPTIC.select);
        }, HOLD_MS);
      }
    }
  };
  const onRawMove = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    const lp = localOf(e);
    pointers.set(e.pointerId, lp);
    if (pinching && pointers.size >= 2) {
      const d = twoFingerDist();
      if (lastPinchDist > 0 && d > 0) {
        const c = centroid();
        cam.zoomAt(c.x, c.y, d / lastPinchDist);
      }
      lastPinchDist = d;
      return;
    }
    // Moving past the slop during a pending hold means it's a drag, not a hold -> cancel it.
    if (
      holdPid === e.pointerId &&
      holdTimer &&
      Math.hypot(lp.x - holdOriginLocal.x, lp.y - holdOriginLocal.y) > HOLD_SLOP
    ) {
      clearHold();
    }
    if (joyPid === e.pointerId) fjoy.move(e.clientX, e.clientY);
  };
  const onRawUp = (e: PointerEvent) => {
    if (!pointers.has(e.pointerId)) return;
    pointers.delete(e.pointerId);
    if (holdPid === e.pointerId) clearHold(); // lifted before HOLD_MS -> it was a tap (move order)
    if (joyPid === e.pointerId) endJoystick();
    if (pinching && pointers.size < 2) {
      pinching = false;
      lastPinchDist = 0;
    }
  };

  // Convert the tap-magnet screen disc (CAMERA.tapMagnetRadiusPx) into a world radius,
  // robust to whatever zoom the camera is at (measured, not derived from a zoom field).
  const magnetWorldRadius = (screen: Pt): number => {
    const a = cam.screenToWorld(screen.x, screen.y);
    const b = cam.screenToWorld(screen.x + CAMERA.tapMagnetRadiusPx, screen.y);
    return Math.hypot(b.x - a.x, b.y - a.y);
  };

  // True when a touch lands on open terrain (no panel, no armed rally, no unit/node under the finger).
  // With nothing selected onRawDown steers immediately; with troops selected a HOLD steers while a
  // quick tap issues a move order.
  const isOpenTerrain = (local: Pt): boolean => {
    if (panelOpen) return false;
    if (armedRallyNodeId) return false; // the next tap is placing a rally point, not steering
    if (armedPlaceTurret) return false; // the next tap is placing a turret, not steering
    const world = cam.screenToWorld(local.x, local.y);
    const r = magnetWorldRadius(local);
    if (sim.pickUnitAt(world, r)) return false; // any unit (you / bot / wild)
    if (sim.pickNodeAt(world, r)) return false;
    return true;
  };

  const announceNode = (nodeId: string | null) => {
    window.dispatchEvent(new CustomEvent('microbe:nodeselect', { detail: { nodeId } }));
  };

  // Transient order feedback: the renderer draws a brief marker at the tapped world point —
  // a teal expanding ring for a MOVE, a red contracting reticle for an ATTACK/focus. (render.ts)
  const ping = (pos: Vec, kind: 'move' | 'attack') => {
    window.dispatchEvent(
      new CustomEvent('microbe:ping', { detail: { x: pos.x, y: pos.y, kind } }),
    );
  };

  const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : 0);
  const screenDist = (aWorld: Vec, bWorld: Vec): number => {
    const a = cam.worldToScreen(aWorld);
    const b = cam.worldToScreen(bWorld);
    return Math.hypot(a.x - b.x, a.y - b.y);
  };
  // Triple-tap: select ALL your live units within a screen radius of the tap (any kind).
  const allInRange = (centerWorld: Vec, radiusPx: number): string[] => {
    const ids: string[] = [];
    for (const o of sim.state.units) {
      if (o.side !== viewSide || o.hp <= 0) continue;
      if (screenDist(centerWorld, o.pos) <= radiusPx) {
        ids.push(o.id);
        if (ids.length >= SELECT.softCap) break;
      }
    }
    return ids;
  };
  // Double-tap: select your units of that kind NEAR the tap (bounded, never map-wide).
  const allOfKindNear = (u: Unit, centerWorld: Vec, radiusPx: number): string[] => {
    const ids: string[] = [];
    for (const o of sim.state.units) {
      if (o.side !== viewSide || o.hp <= 0 || o.kind !== u.kind) continue;
      if (screenDist(centerWorld, o.pos) <= radiusPx) {
        ids.push(o.id);
        if (ids.length >= SELECT.softCap) break;
      }
    }
    return ids;
  };
  // Selection radii are a fraction of the smaller viewport dimension, so double/triple grabs stay
  // bounded to roughly what's on screen instead of sweeping the whole tall map.
  const selRadius = (frac: number): number => frac * Math.min(cam.viewW, cam.viewH);

  let lastTapAt = -1;
  let tapCount = 0; // consecutive taps on the same unit (drives 1/2/3-tap selection escalation)
  let lastTapUnitId: string | null = null;
  // Double-tap open water (while a band is selected) = fast DESELECT. Tracks the last empty-water
  // move-tap; a second water tap within the double-tap window clears instead of moving again. The
  // snapshot lets that clear UNDO the throwaway first tap's move, so a band already en route to a
  // real destination keeps it instead of being redirected to the double-tap spot.
  let lastWaterTapAt = -1;
  let waterMoveSnapshot: OrderSnapshot[] = [];
  // Double-tap your OWN node: the 1st tap garrisons the band to base; a quick 2nd tap on the SAME
  // node UNDOES that garrison (band resumes its prior task) and opens the node menu instead. The
  // snapshot captures the pre-garrison orders so the undo restores them.
  let lastNodeTapId: string | null = null;
  let lastNodeTapAt = -1;
  let nodeGarrisonSnapshot: OrderSnapshot[] = [];

  // Capture the current selection's movement orders (so a stray move can be rolled back).
  const snapshotSelectionOrders = (): OrderSnapshot[] => {
    const out: OrderSnapshot[] = [];
    for (const id of sim.state.selectionBySide[viewSide]) {
      const u = sim.unitById(id);
      if (!u) continue;
      out.push({
        id,
        moveTarget: u.moveTarget ? { x: u.moveTarget.x, y: u.moveTarget.y } : null,
        moveSpeedCap: u.moveSpeedCap,
        focusId: u.focusId,
        attackNodeId: u.attackNodeId,
        assignedNode: u.assignedNode,
        bandStance: u.bandStance,
      });
    }
    return out;
  };

  // ---------- the smart tap ----------
  const onTap = (p: Pt) => {
    if (suppressNextTap) {
      suppressNextTap = false;
      return;
    }
    if (pointers.size > 1) return; // genuine multi-touch in progress; ignore

    const world: Vec = cam.screenToWorld(p.x, p.y);
    const r = magnetWorldRadius(p);

    // Rally-arm intercepts everything: the next tap places the armed node's rally point.
    if (armedRallyNodeId) {
      const nid = armedRallyNodeId;
      armedRallyNodeId = null;
      sim.command({ t: 'rally', nodeId: nid, pos: world });
      haptic(HAPTIC.command);
      return;
    }
    // Turret-placement arm: the next tap places a home turret (sim clamps it to the home ring + validates).
    // The sim may REJECT it (too close to another turret, at cap, unaffordable), so only celebrate on a
    // real placement — otherwise the success ping/haptic would lie. Detect via the turret-count delta.
    if (armedPlaceTurret) {
      armedPlaceTurret = false;
      const before = sim.state.turrets.length;
      sim.command({ t: 'placeTurret', pos: world });
      const placed = sim.state.turrets.length > before;
      if (placed) {
        const t = sim.state.turrets[sim.state.turrets.length - 1];
        ping(t.pos, 'move'); // at the clamped ring spot, not the raw tap
        haptic(HAPTIC.command);
      } else {
        haptic(HAPTIC.select); // rejected — a soft tick, no misleading placement ping
      }
      return;
    }

    // Consume the water-double-tap timer up front: any tap resets it, so only two CONSECUTIVE
    // open-water taps chain into a deselect (an intervening unit/node tap breaks the chain).
    const prevWaterTapAt = lastWaterTapAt;
    lastWaterTapAt = -1;

    // Gather magnet candidates.
    const enemyUnit = sim.pickUnitAt(world, r, foeSide);
    const wildUnit = sim.pickUnitAt(world, r, 'wild'); // farm target: focus it to graze amino
    const ownUnit = sim.pickUnitAt(world, r, viewSide);
    const node = sim.pickNodeAt(world, r);
    const enemyNode = node && node.owner === foeSide ? node : null;
    const neutralNode = node && node.owner === 'neutral' ? node : null;
    const ownNode = node && node.owner === viewSide ? node : null;

    // For SELECTING a node (nothing in hand), the whole defended footprint is tappable — out to the
    // node's outer defense ring — and a DIRECT hit on a unit's body (magnet off, radius 0) is what lets
    // a parked unit still be picked individually. A tap that lands on the node but not on a unit selects
    // the node, so a friendly parked inside no longer makes the node impossible to select.
    const selNode = sim.pickNodeAt(world, r, true);
    // A "direct" unit tap uses a TIGHT magnet (half the normal disc) so a parked unit stays comfortably
    // tappable when zoomed out, while a tap clearly out on the node's ring still falls to the node.
    const directOwnUnit = sim.pickUnitAt(world, r * 0.5, viewSide);

    // ---- repeat-tap escalation on your units: 1 tap = just that unit, 2 = all of its kind,
    //      3 = all your units in range of the tap. Only escalates while re-tapping the SAME unit
    //      within the double-tap window; anything else resets the count. ----
    const t = nowMs();
    // Escalate on repeat taps of the SAME unit within the window — keyed on unit id, not finger
    // position, so you can follow a moving unit and still double/triple-tap it.
    const sameUnit = !!ownUnit && lastTapUnitId === ownUnit.id;
    const chained = t - lastTapAt <= SELECT.doubleTapMs && sameUnit;
    tapCount = chained ? tapCount + 1 : 1;
    lastTapAt = t;
    lastTapUnitId = ownUnit ? ownUnit.id : null;

    // ---- your UNIT: with NO selection, a tap selects it (repeat taps escalate to its kind / everyone in
    //      range). With a selection LIVE, a chained re-tap still escalates; a tap on a unit already IN the
    //      band deselects; and a plain tap on a DIFFERENT friendly falls through to COMMAND mode so the
    //      band MOVES there instead of re-selecting (was: always re-select). ----
    const ownUnitPri = ownUnit ? TAP_PRIORITY.ownUnit : -1;
    const ownNodePri = ownNode ? TAP_PRIORITY.ownNode : -1;
    const choseUnit = !!ownUnit && ownUnitPri >= ownNodePri;
    const hasSelection = sim.state.selectionBySide[viewSide].length > 0;
    const tappedSelected = !!ownUnit && sim.state.selectionBySide[viewSide].includes(ownUnit.id);

    // ---- YOUR NODE + a live selection -> GARRISON there to turtle + clear the selection. This runs
    //      BEFORE the unit block so a friendly parked ON the node (which the tap magnet co-picks, and
    //      which always out-priorities the node) can't hijack the gesture into a select/deselect. A
    //      chained tap is left to selection-escalation. ----
    if (ownNode && hasSelection && !chained) {
      // 1st tap: return the band to base. Snapshot their PRIOR orders first so a quick 2nd tap on
      // the same node can UNDO the garrison and open the node menu without disturbing the band.
      nodeGarrisonSnapshot = snapshotSelectionOrders();
      lastNodeTapId = ownNode.id;
      lastNodeTapAt = t;
      sim.command({ t: 'garrison', nodeId: ownNode.id });
      sim.command({ t: 'clearSelection' });
      announceNode(null);
      ping(ownNode.pos, 'move');
      haptic(HAPTIC.command);
      return;
    }
    // 2nd tap on that SAME node (band already garrisoned + deselected): UNDO the garrison so the band
    // resumes what it was doing, and open the node menu. Keeps the selection cleared (view switches to
    // node options) — e.g. a Queen mid-fight keeps fighting while you inspect home.
    if (
      ownNode && !hasSelection && !directOwnUnit &&
      lastNodeTapId === ownNode.id && t - lastNodeTapAt <= SELECT.doubleTapMs &&
      nodeGarrisonSnapshot.length
    ) {
      sim.command({ t: 'restoreOrders', orders: nodeGarrisonSnapshot });
      nodeGarrisonSnapshot = [];
      lastNodeTapId = null;
      announceNode(ownNode.id);
      haptic(HAPTIC.select);
      return;
    }
    // ---- NOTHING selected + the tap lands on a node (out to its defense ring) and NOT directly on a
    //      unit -> SELECT/inspect that node. Runs before the unit block so a friendly parked inside the
    //      node can't hijack the gesture; a direct tap on a unit's body (directOwnUnit) still selects it. ----
    if (!hasSelection && selNode && !directOwnUnit) {
      announceNode(selNode.id);
      haptic(HAPTIC.select);
      return;
    }

    if (ownUnit && ownUnitPri >= ownNodePri) {
      if (chained) {
        // deliberate escalation: grow selection to this unit's kind (2 taps) / all in range (3 taps)
        const ids =
          tapCount >= 3
            ? allInRange(world, selRadius(SELECT.rangeRadiusFrac))
            : allOfKindNear(ownUnit, world, selRadius(SELECT.kindRadiusFrac));
        sim.command({ t: 'select', ids });
        announceNode(null);
        haptic(HAPTIC.select);
        return;
      }
      if (!hasSelection) {
        // fresh pick — nothing selected yet
        sim.command({ t: 'select', ids: [ownUnit.id] });
        announceNode(null);
        haptic(HAPTIC.select);
        return;
      }
      if (tappedSelected && !enemyUnit && !enemyNode && !neutralNode) {
        // tapping a live member of your band (with no enemy/neutral node also under the tap) clears
        // the selection. The !neutralNode guard is load-bearing: your squad stands ON the home
        // crater it's colonizing, so a tap there must fall through to CLAIM, not deselect.
        sim.command({ t: 'clearSelection' });
        haptic(HAPTIC.select);
        return;
      }
      // selection live + tap landed DIRECTLY on a DIFFERENT friendly body -> SELECT that unit
      // instead of ordering the current band there. Selecting never issues a move, so the prior
      // selection keeps its orders untouched: stationary stays put, en route stays en route.
      // Uses the tight direct-body magnet (directOwnUnit), so a looser tap on water NEAR your units
      // still falls through to a move — only a deliberate tap on the body re-selects.
      if (directOwnUnit && !enemyUnit && !enemyNode && !neutralNode) {
        sim.command({ t: 'select', ids: [directOwnUnit.id] });
        announceNode(null);
        haptic(HAPTIC.select);
        return;
      }
      // else: selection live + a loose-magnet tap near a DIFFERENT friendly (or an enemy is under the
      //       tap) -> fall through to the COMMAND block below (move / focus / attack)
    }

    // ---- your NODE, NOTHING selected -> open its dock panel (produce / build / rally). (The
    //      units-selected case already garrisoned + returned above.) Skipped if the tap actually
    //      chose a unit so tapping a parked unit still selects it. ----
    if (ownNode && !choseUnit) {
      announceNode(ownNode.id);
      haptic(HAPTIC.select);
      return;
    }

    if (hasSelection) {
      // ---- COMMAND mode: the tap is an order for the current selection ----
      const foeUnit = enemyUnit ?? wildUnit; // focus a rival OR a wild grazer to farm it
      if (foeUnit) {
        sim.command({ t: 'focus', enemyId: foeUnit.id });
        ping(foeUnit.pos, 'attack');
        haptic([...HAPTIC.attack]); // config tuple is readonly; copy for the mutable-array sink
      } else if (enemyNode) {
        // Siege needs warriors (only they erode a core). With a warrior-less selection, fall back to a
        // plain move so the tap still does something visible instead of a silent no-op.
        const hasWarrior = sim.state.units.some(
          (u) => u.kind !== 'healer' && u.hp > 0 && sim.state.selectionBySide[viewSide].includes(u.id),
        );
        if (hasWarrior) {
          sim.command({ t: 'attack', nodeId: enemyNode.id }); // siege: melee close to the core, ranged shell it from range
          ping(enemyNode.pos, 'attack');
          haptic([...HAPTIC.attack]);
        } else {
          sim.command({ t: 'move', pos: enemyNode.pos });
          ping(enemyNode.pos, 'move');
          haptic(HAPTIC.command);
        }
      } else if (neutralNode) {
        sim.command({ t: 'claim', nodeId: neutralNode.id });
        ping(neutralNode.pos, 'move');
        haptic(HAPTIC.command);
      } else if (!ownUnit && prevWaterTapAt >= 0 && t - prevWaterTapAt <= SELECT.doubleTapMs) {
        // Second quick tap on genuine OPEN WATER — DESELECT. First UNDO the throwaway first-tap move
        // so a band already en route to a real destination keeps it. The !ownUnit guard is essential:
        // a fall-through tap onto a DIFFERENT friendly must stay a move, not misfire this deselect.
        if (waterMoveSnapshot.length) sim.command({ t: 'restoreOrders', orders: waterMoveSnapshot });
        waterMoveSnapshot = [];
        sim.command({ t: 'clearSelection' });
        announceNode(null);
        haptic(HAPTIC.select);
      } else {
        // A move order. Arm the double-tap-to-clear window ONLY for a genuine open-water tap — a
        // fall-through tap onto a friendly is just a reposition and must not arm/trigger the deselect.
        if (!ownUnit) {
          waterMoveSnapshot = snapshotSelectionOrders();
          lastWaterTapAt = t;
        }
        sim.command({ t: 'move', pos: world });
        ping(world, 'move');
        haptic(HAPTIC.command);
      }
      return;
    }

    // ---- nothing selected: inspect a neutral/enemy node, else empty water is a no-op ----
    const inspect = neutralNode ?? enemyNode;
    if (inspect) {
      announceNode(inspect.id);
      haptic(HAPTIC.select);
    } else {
      announceNode(null);
    }
  };

  // ---------- tap via attachInput (map = tap-only commands; camera pans via the joystick) ----------
  const handle = attachInput(canvas, {
    tapSlop: 12,
    swipeThreshold: 10_000, // never classify as a swipe; a stray one-finger drag on the map is a no-op
    onTap,
  });

  // ---------- cross-UI events ----------
  const onRecenter = () => {
    const homeId = sim.state.colonies[viewSide].homeId;
    const home = sim.nodeById(homeId);
    if (home) {
      cam.recenter(home.pos);
      return;
    }
    // No home colonized yet — recenter on the dropped squad instead.
    let cx = 0;
    let cy = 0;
    let n = 0;
    for (const u of sim.state.units) {
      if (u.side === viewSide && u.hp > 0) { cx += u.pos.x; cy += u.pos.y; n++; }
    }
    if (n > 0) cam.recenter({ x: cx / n, y: cy / n });
  };
  const onRally = (e: Event) => {
    const nid = (e as CustomEvent<{ nodeId: string }>).detail?.nodeId;
    if (nid) armedRallyNodeId = nid;
  };
  const onPlaceTurret = () => { armedPlaceTurret = true; };

  // ---- camera LOCK: while on, the camera tracks the live selection's centroid every frame ----
  // The lock button lives in the HUD control bar; it fires 'microbe:camlock-toggle' and we echo
  // 'microbe:camlock' {on} back so the button can reflect the state. A self-scheduling rAF sets the
  // camera's follow target (render.ts's cam.update does the actual easing) — it's a no-op with no
  // selection, so a lock held through a deselect simply re-follows the next units you pick.
  let cameraLocked = false;
  let followRaf = 0;
  const selectionCentroid = (): Vec | null => {
    let cx = 0;
    let cy = 0;
    let n = 0;
    for (const id of sim.state.selectionBySide[viewSide]) {
      const u = sim.unitById(id);
      if (u && u.hp > 0) { cx += u.pos.x; cy += u.pos.y; n++; }
    }
    return n > 0 ? { x: cx / n, y: cy / n } : null;
  };
  const followTick = () => {
    if (!cameraLocked) { followRaf = 0; return; }
    // Yield to an active MANUAL camera gesture (pinch-zoom or the steer joystick) so the user can
    // still adjust the view while locked — otherwise follow would overwrite the target every frame
    // and the gesture couldn't move the camera. Follow resumes the frame after they let go.
    if (!pinching && joyPid === -1) {
      const c = selectionCentroid();
      if (c) cam.follow(c);
    }
    followRaf = requestAnimationFrame(followTick);
  };
  const onCamLockToggle = () => {
    cameraLocked = !cameraLocked;
    window.dispatchEvent(new CustomEvent('microbe:camlock', { detail: { on: cameraLocked } }));
    haptic(HAPTIC.select);
    if (cameraLocked && !followRaf) followRaf = requestAnimationFrame(followTick);
  };
  // Explicit "look elsewhere" jumps (home-cycle) release the lock — otherwise follow snaps the
  // camera straight back to the selection and the jump reads as a dead button. Echoes to the HUD.
  const unlockCamera = () => {
    if (!cameraLocked) return;
    cameraLocked = false;
    window.dispatchEvent(new CustomEvent('microbe:camlock', { detail: { on: false } }));
  };

  // ---- home / node CYCLE: quick taps jump the camera home, then round-robin your craters ----
  // A tap after a pause restarts at Home; rapid taps advance: home → crater → crater → … → home.
  // Before any home is colonized, fall back to recentering on the dropped squad.
  let cycleIdx = -1;
  let lastCycleAt = -1;
  const onHomeCycle = () => {
    unlockCamera(); // this button means "look at a node", not "follow my units"
    const you = sim.state.colonies[viewSide];
    const home = sim.nodeById(you.homeId) ?? null;
    const owned = sim.state.nodes.filter((n) => n.owner === viewSide);
    if (owned.length === 0) { onRecenter(); return; }
    const rest = owned
      .filter((n) => n !== home)
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const ordered: WorldNode[] = home ? [home, ...rest] : rest;
    const t = nowMs();
    if (t - lastCycleAt > 1200 || cycleIdx < 0) cycleIdx = 0;
    else cycleIdx = (cycleIdx + 1) % ordered.length;
    lastCycleAt = t;
    const target = ordered[cycleIdx] ?? ordered[0];
    if (target) cam.recenter(target.pos);
    haptic(HAPTIC.select);
  };

  // ---- QUEEN / DRONE cycle: same round-robin as home-cycle, but over your live drones (a Queen is a
  // drone with queen:true, so this covers both). Queens sort first, then by id; the camera pans to
  // each in turn. A tap after a pause restarts at the first; rapid taps advance one per tap. ----
  let queenCycleIdx = -1;
  let lastQueenCycleAt = -1;
  const onQueenCycle = () => {
    unlockCamera(); // "look at a drone", not "follow my current selection"
    const drones = sim.state.units
      .filter((u) => u.side === viewSide && u.hp > 0 && u.kind === 'drone')
      .sort((a, b) =>
        Number(b.queen) - Number(a.queen) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
      );
    if (drones.length === 0) { onRecenter(); return; }
    const t = nowMs();
    if (t - lastQueenCycleAt > 1200 || queenCycleIdx < 0) queenCycleIdx = 0;
    else queenCycleIdx = (queenCycleIdx + 1) % drones.length;
    lastQueenCycleAt = t;
    const target = drones[queenCycleIdx] ?? drones[0];
    if (target) cam.recenter(target.pos);
    haptic(HAPTIC.select);
  };
  const onPanel = (e: Event) => {
    panelOpen = !!(e as CustomEvent<{ open: boolean }>).detail?.open;
    if (panelOpen) {
      endJoystick(); // panel just took over — retract any live steer pad
      clearHold();
    }
  };

  canvas.addEventListener('pointerdown', onRawDown);
  canvas.addEventListener('pointermove', onRawMove);
  canvas.addEventListener('pointerup', onRawUp);
  canvas.addEventListener('pointercancel', onRawUp);
  window.addEventListener('microbe:recenter', onRecenter);
  window.addEventListener('microbe:rally', onRally);
  window.addEventListener('microbe:panel', onPanel);
  window.addEventListener('microbe:camlock-toggle', onCamLockToggle);
  window.addEventListener('microbe:home-cycle', onHomeCycle);
  window.addEventListener('microbe:queen-cycle', onQueenCycle);
  window.addEventListener('microbe:place-turret', onPlaceTurret);

  return {
    destroy() {
      clearHold();
      endJoystick();
      if (followRaf) cancelAnimationFrame(followRaf);
      handle.destroy();
      canvas.removeEventListener('pointerdown', onRawDown);
      canvas.removeEventListener('pointermove', onRawMove);
      canvas.removeEventListener('pointerup', onRawUp);
      canvas.removeEventListener('pointercancel', onRawUp);
      window.removeEventListener('microbe:recenter', onRecenter);
      window.removeEventListener('microbe:rally', onRally);
      window.removeEventListener('microbe:panel', onPanel);
      window.removeEventListener('microbe:camlock-toggle', onCamLockToggle);
      window.removeEventListener('microbe:home-cycle', onHomeCycle);
      window.removeEventListener('microbe:queen-cycle', onQueenCycle);
      window.removeEventListener('microbe:place-turret', onPlaceTurret);
    },
  };
}
